import { Layout, Table, Typography, Col } from "antd";
import React, { forwardRef, useCallback, useImperativeHandle, useState } from "react";
import axios from "api/axios";
import { useDpatIndicatorDataSource } from "hooks/useDpatIndicatorDataSource";
import {
  calculatePercentage,
  formatDataGeneral,
  getAttributeValue,
  getYearFromDateValue,
  isDateInSelectedYear,
} from "utils/utils";
import DpatSubIndicatorSection from "../setup/DpatSubIndicatorSection";
import DpatIndicatorCmsIntro from "../setup/DpatIndicatorCmsIntro";
import MaxScoreDisplay from "../setup/MaxScoreDisplay";
import ActualScoreDisplay from "../setup/ActualScoreDisplay";
import IndicatorScoreSection from "../setup/IndicatorScoreSection";
import { renderDpatEvidenceLink, scoreOnlyIfRowEvidenceComplete } from "utils/renderDpatEvidenceLink";
import Comment from "components/Comments";

const PI_HEALTH_SLUG = "pi4.0-4.2";
const HEALTH_FACILITY_PROGRAM = "cKOJDISfpX2";
const SUPPORT_DATA_ELEMENT = "xAySRtnwzQa";
const SUPPORT_EVIDENCE_ELEMENT = "E6mSis1p8NG";

function hasSupportValue(support) {
  const value = String(support ?? "").trim();
  if (!value) {
    return false;
  }
  return !/^(n\/?a|none|nil|null|-)$/i.test(value);
}

function getOccurredAtYear(occurredAt) {
  if (occurredAt == null || occurredAt === "") {
    return null;
  }
  const match = String(occurredAt).match(/^(\d{4})/);
  if (match) {
    return Number(match[1]);
  }
  return getYearFromDateValue(occurredAt);
}

function isOccurredAtInSelectedYear(occurredAt, selectedYear) {
  return isDateInSelectedYear(occurredAt, selectedYear);
}

function getDataValue(event, dataElementId) {
  return event?.dataValues?.find((dv) => dv.dataElement === dataElementId)?.value || "";
}

/** Prefer the latest in-year timeline event that has a support value. */
function getSupportTimelineForYear(reports, trackedEntity, selectedYear) {
  const yearEvents = (reports || [])
    .filter((event) => (
      event.trackedEntity === trackedEntity
      && isOccurredAtInSelectedYear(event.occurredAt, selectedYear)
    ))
    .sort((a, b) => String(b.occurredAt || "").localeCompare(String(a.occurredAt || "")));

  const supportEvent = yearEvents.find((event) => hasSupportValue(getDataValue(event, SUPPORT_DATA_ELEMENT)))
    || yearEvents[0]
    || null;

  if (!supportEvent) {
    return { support: "", reportLink: "" };
  }

  const support = getDataValue(supportEvent, SUPPORT_DATA_ELEMENT);
  const hasEvidenceFile = supportEvent.dataValues?.some(
    (dv) => dv.dataElement === SUPPORT_EVIDENCE_ELEMENT && dv.value,
  );

  return {
    support: hasSupportValue(support) ? support : "",
    reportLink: hasEvidenceFile ? supportEvent.event : "",
  };
}

const HealthServiceSupport = forwardRef(({
  year,
  districtId,
  hideComment,
  savedAssessmentData,
  assessmentStartLoading,
  isAssessmentStarted,
}, ref) => {
  const { Content } = Layout;
  const { Title, Text } = Typography;
  const [healthFacilities, setHealthFacilities] = useState([]);
  const [healthFacilitySupport, setHealthFacilitySupport] = useState([]);
  const [percentage, setPercentage] = useState(0);
  const [score, setScore] = useState(0);
  const [maxScore, setMaxScore] = useState(1);

  const handleCmsIndicator = useCallback((ind) => {
    const ms = Number(ind?.maxScore);
    if (Number.isFinite(ms) && ms > 0) setMaxScore(ms);
    else setMaxScore(1);
  }, []);

  useImperativeHandle(ref, () => ({
    getData: () => ({
      indicator: "PI4",
      area: "Access to Social Services",
      maxScore,
      healthFacilities,
      healthFacilitySupport,
      score,
      percentage
    }),
  }));

  const healthColumns = [
    {
      title: 'No',
      dataIndex: 'no',
      key: 'no',
    },
    {
      title: 'Facility Name',
      dataIndex: 'facility',
      key: 'facility',
    },
    {
      title: 'Location',
      dataIndex: 'location',
      key: 'location',
    },
    {
      title: 'Support(s)',
      dataIndex: 'support',
      key: 'support',
    },
    {
      title: 'Support Evidence',
      dataIndex: 'report',
      key: 'report',
      render: (value) => renderDpatEvidenceLink(value, "View Evidence"),
    }
  ];

  const healthSupportColumns = [
    {
      title: "No. of Health Centres (A)",
      dataIndex: "noOfHealthCenter",
      key: "noOfHealthCenter"
    },
    {
      title: "No. of Health Centres supported by DA (B)",
      dataIndex: "noOfHealthCenterSupported",
      key: "noOfHealthCenterSupported"
    },
    {
      title: "% of health centres supported B/A * 100",
      dataIndex: "percentage",
      key: "percentage"
    }
  ];

  const loadFromDhis2 = useCallback(() => {
    if (!districtId || !year) return;
    axios
      .get(`/tracker/trackedEntities?orgUnit=${districtId}&program=${HEALTH_FACILITY_PROGRAM}&pageSize=5000`)
      .then(result => {
        if (result.data.instances.length > 0) {
          axios
            .get(`/tracker/events?program=${HEALTH_FACILITY_PROGRAM}&orgUnit=${districtId}&pageSize=10000`)
            .then(resp => {
              const hospitals = result.data.instances;
              const reports = resp.data.instances;
              const temp = [];

              const publicHospitals = formatDataGeneral(hospitals, "Ownership Type", "Public") || [];

              publicHospitals.forEach((facility, idx) => {
                const { support, reportLink } = getSupportTimelineForYear(
                  reports,
                  facility.trackedEntity,
                  year,
                );

                const tempDataSet = {
                  no: idx + 1,
                  facility: getAttributeValue("Name", facility),
                  location: getAttributeValue("Location", facility),
                  support,
                  report: reportLink
                    ? `https://dddp.gov.gh/api/events/files?eventUid=${reportLink}&dataElementUid=${SUPPORT_EVIDENCE_ELEMENT}`
                    : "Not Uploaded",
                };

                temp.push(tempDataSet);
              });

              setHealthFacilities(temp);

              const supportedRows = temp.filter((facility) => hasSupportValue(facility.support));
              const facilitiesSupported = supportedRows.length;
              const percentageNum = Number(
                calculatePercentage(facilitiesSupported, temp.length).toFixed(2),
              );
              const rawScore = percentageNum >= 15 ? 1 : 0;

              setPercentage(percentageNum.toFixed(2));
              // Score only when every support in Assembly Support to Public Health Facilities has evidence.
              setScore(scoreOnlyIfRowEvidenceComplete(supportedRows, ["report"], rawScore));

              setHealthFacilitySupport([{
                noOfHealthCenter: temp.length,
                noOfHealthCenterSupported: facilitiesSupported,
                percentage: percentageNum.toFixed(2),
              }]);
            })
            .catch(err => console.log(err))
        } else {
          setHealthFacilities([]);
          setHealthFacilitySupport([]);
          setPercentage(0);
          setScore(0);
        }
      })
      .catch(err => console.log(err))
  }, [districtId, year]);

  useDpatIndicatorDataSource({
    isAssessmentStarted,
    savedAssessmentData,
    assessmentStartLoading,
    loadFromDhis2,
    applySnapshot: (snap) => {
      if (snap.healthFacilities) setHealthFacilities(snap.healthFacilities);
      if (snap.healthFacilitySupport) setHealthFacilitySupport(snap.healthFacilitySupport);
      if (snap.score != null) setScore(snap.score);
      if (snap.percentage != null) setPercentage(snap.percentage);
      if (snap.maxScore != null) setMaxScore(snap.maxScore);
    },
    deps: [year, districtId, loadFromDhis2],
  });

  return (
    <Comment
      data={healthFacilitySupport}
      year={year}
      districtId={districtId}
      tableCommentedId={`pi4.0-4.2-${year}`}
      hideComment={hideComment}
    >
      {({ renderCommentFeedbackFooter }) => (
        <DpatSubIndicatorSection code={PI_HEALTH_SLUG}>
          <DpatIndicatorCmsIntro
            year={year}
            slug={PI_HEALTH_SLUG}
            onCmsIndicator={handleCmsIndicator}
            mainTitleStyle={{ marginTop: "20px" }}
          />

          <IndicatorScoreSection>
            <MaxScoreDisplay score={maxScore} />
            <ActualScoreDisplay slug={PI_HEALTH_SLUG} label="PI 4.0-4.2 Actual Score" score={score} />
          </IndicatorScoreSection>

          <Title level={5} style={{ marginTop: "20px" }}>
            Assembly Support to Public Health Facilities
          </Title>
          <Table
            columns={healthColumns}
            dataSource={healthFacilities || []}
            pagination={false} bordered />

          <Title level={5} style={{ marginTop: "20px" }}>
            List of Public Health Facilities
          </Title>
          <Table
            columns={healthSupportColumns}
            dataSource={healthFacilitySupport}
            pagination={false} bordered />

          <Title level={5} style={{ marginTop: "30px" }}>Conclusion:</Title>
          <Content>
            The Assembly has supported {percentage}% of Public Health Facilities to address their challenges in {year}
          </Content>

          {!hideComment && renderCommentFeedbackFooter()}
        </DpatSubIndicatorSection>
      )}
    </Comment>
  );
})

export default HealthServiceSupport;
